import { useEffect, useState, useMemo } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import moment from "moment";
import "moment/locale/pt-br";
import toast from "react-hot-toast";
import XLSX from "xlsx-js-style";
import { Tooltip } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ModalEditarDadosCredencial from "../../../../clientes/single/components/OrcamentosComponent/components/ModalEditarDadosCredencial/ModalEditarDadosCredencial";
import PdfCartaApresentacao from "../../../../../shared/components/PdfGenerator/PdfCartaApresentacao/PdfCartaApresentacao";

// Material UI imports
import {
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import SendIcon from "@mui/icons-material/Send";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

// Configurar locale do moment para português
moment.locale("pt-br");

// ⭐ HOOK DE DEBOUNCE
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function RelatorioCredenciais({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  gerarPdfComprovativoDesconto,
  unidadeSelecionada = "",
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogoEnvioAberto, setDialogoEnvioAberto] = useState(false);
  const [dialogoDesmarcarAberto, setDialogoDesmarcarAberto] = useState(false);
  const [dadosEnvio, setDadosEnvio] = useState({
    numeroFatura: "",
    mesReferencia: moment().format("MMMM YYYY"),
  });
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [instituicoes, setInstituicoes] = useState([]);
  const [selectedInstituicao, setSelectedInstituicao] = useState();
  const [instituicao, setInstituicao] = useState();

  const [configExportacao, setConfigExportacao] = useState({
    organizarPorCategoria: false,
    orcamentosPorFolha: 15,
    calcularSoExecutados: true,
  });
  const [dialogoExportacaoAberto, setDialogoExportacaoAberto] = useState(false);
  const [dialogoComprovativoDescontoAberto, setDialogCompravitoDescontoAberto] =
    useState(false);
  const [dataComprovativo, setDataComprovativo] = useState(moment());

  const [selecaoInterna, setSelecaoInterna] = useState([]);

  //states para modal de edição de dados de credencial
  const [modalCredencialAberto, setModalCredencialAberto] = useState(false);
  const [dadosCredencialEdicao, setDadosCredencialEdicao] = useState(null);

  //Carta de apresentação
  const [dialogoCartaAberto, setDialogoCartaAberto] = useState(false);
  const [dataCartaApresentacao, setDataCartaApresentacao] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [mostrarCartaApresentacao, setMostrarCartaApresentacao] =
    useState(false);

  // ⭐ DEBOUNCE DO SEARCH TERM
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // ⭐ FUNÇÃO AUXILIAR
  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  // ⭐ Calcula a classe visual com base na data de credencial e datas de execução
  const calcularClasseCredencial = ({
    dataCredencial,
    dataInicio,
    jaEnviado,
  }) => {
    if (jaEnviado) return "enviado";

    const dtCredencial = moment(dataCredencial);
    const dtInicio = moment(dataInicio || dataCredencial);
    const diasDesdeCredencial = moment().diff(dtCredencial, "days");
    const diasAtraso = dtInicio.diff(dtCredencial, "days");

    if (diasDesdeCredencial >= 180) return "expirou";
    if (diasAtraso >= 30) return "inicio-atrasado";
    if (diasDesdeCredencial >= 150) return "ajustar";
    return "";
  };

  // ⭐ PRÉ-PROCESSAR DADOS (MEMOIZADO)
  const rowsNormalizados = useMemo(() => {
    return rows.map((row) => ({
      ...row,
      nomeNormalizado: removeAccents(row.nome.toLowerCase()),
      credencialNormalizada:
        row.nrCredencial?.replace(/\s+/g, "").toLowerCase() || "",
    }));
  }, [rows]);

  // ⭐ FILTRAR ROWS (MEMOIZADO)
  const rowsFiltradas = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return rowsNormalizados;
    }

    const searchTerms = removeAccents(debouncedSearchTerm.toLowerCase()).split(
      /\s+/,
    );

    return rowsNormalizados.filter((row) => {
      // Busca por nome (palavra por palavra)
      const nomeWords = row.nomeNormalizado.split(/\s+/);
      const matchesName = searchTerms.every((term) =>
        nomeWords.some((word) => word.includes(term)),
      );

      // Busca por credencial
      const matchesCredencial = searchTerms.every((term) =>
        row.credencialNormalizada.includes(term),
      );

      return matchesName || matchesCredencial;
    });
  }, [rowsNormalizados, debouncedSearchTerm]);

  // Este useEffect executa quando o componente é montado ou quando a ação muda
  useEffect(() => {
    if (acao) {
      if (acao === "gerar") {
        gerarRelatorio();
      } else if (acao === "exportar") {
        exportarFicheiro();
      }
    }
  }, [acao, atualizacoes]);

  useEffect(() => {
    const buscarInstituicoes = async () => {
      try {
        startLoading();
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/instituicoes/clinica/${dadosClinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );

        setInstituicoes(
          responseData.instituicoes.map((i) => ({
            value: i._id,
            label: i.nome,
          })),
        );

        const idInps = responseData.instituicoes.find(
          (i) => i.nome.toLowerCase() === "inps",
        )?._id;

        setSelectedInstituicao(idInps);
        setInstituicao(responseData.instituicoes?.[0] || []);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };

    buscarInstituicoes();
  }, []);

  // ⭐ OTIMIZAR USEEFFECT PARA SELEÇÃO INTERNA
  useEffect(() => {
    const idsVisiveis = new Set(rowsFiltradas.map((r) => r.id));
    setSelecaoInterna(selecionados.filter((id) => idsVisiveis.has(id)));
  }, [selecionados, rowsFiltradas]);

  const handleSelectionChange = (newSelection) => {
    const idsVisiveis = new Set(rowsFiltradas.map((r) => r.id));

    // IDs que estavam selecionados mas NÃO estão visíveis (preservar sempre)
    const selecoesNaoVisiveis = selecionados.filter(
      (id) => !idsVisiveis.has(id),
    );

    // IDs que estavam selecionados E estão visíveis
    const selecoesVisiveisAnteriores = selecionados.filter((id) =>
      idsVisiveis.has(id),
    );

    // Detectar o que foi adicionado ou removido
    const adicionados = newSelection.filter(
      (id) => !selecaoInterna.includes(id),
    );
    const removidos = selecaoInterna.filter((id) => !newSelection.includes(id));

    // Calcular nova seleção de itens visíveis
    let novasSelecoes = selecoesVisiveisAnteriores
      .filter((id) => !removidos.includes(id))
      .concat(
        adicionados.filter((id) => {
          const row = rowsNormalizados.find((r) => r.id === id);
          if (!row) return false;
          // Se filtro "enviados", permite selecionar enviados (para desmarcar)
          if (filtroStatus === "enviados") return true;
          return !row.jaEnviado;
        }),
      );

    // Combinar com não-visíveis
    const combinado = [...new Set([...selecoesNaoVisiveis, ...novasSelecoes])];

    setSelecionados(combinado);
  };

  const gerarRelatorio = async () => {
    if (!dadosClinica?._id) return;

    setError(null);
    startLoading();
    try {
      const formData = new FormData();
      formData.append("startDate", dataFiltro.dataInicial.toISOString());
      formData.append("endDate", dataFiltro.dataFinal.toISOString());
      formData.append("filtroStatus", filtroStatus);
      if (selectedInstituicao) {
        formData.append("instituicaoId", selectedInstituicao);
      }
      // Vazio = consolidado da empresa. Filtra pela unidade onde o orçamento
      // foi criado.
      if (unidadeSelecionada) {
        formData.append("estabelecimento", unidadeSelecionada);
      }

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/getDadosRelatorioCredenciaisInstituicao/${dadosClinica._id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      setInstituicao(responseData.instituicao);

      const tempCredenciaisData = [];
      const tempRows = [];

      responseData.orcamentos.forEach((o, index) => {
        const dateStyle = {
          alignment: { horizontal: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };

        const classe = calcularClasseCredencial({
          dataCredencial: o.dataCredencial,
          dataInicio: o.dataInicio,
          jaEnviado: o.envioInstituicao?.enviado === true,
        });

        // Aplica cor no estilo do Excel conforme a classe
        if (classe === "expirou") {
          dateStyle.font = { color: { rgb: "FF0000" } };
        } else if (classe === "inicio-atrasado") {
          dateStyle.font = { color: { rgb: "9C27B0" } };
        } else if (classe === "ajustar") {
          dateStyle.font = { color: { rgb: "FFA500" } };
        }

        const majoracao = Number(o.majoracao) || 0;
        const totalBeneficiarioFinal =
          o.totalBeneficiario + o.totalProtesesBeneficiario - majoracao;
        const totalInpsFinal =
          o.totalInstituicao + o.totalProtesesInstituicao + majoracao;

        const xlsRow = [
          index + 1,
          o.cliente.nome,
          ensureValue(o.numeroCredencial),
          {
            v:
              o.dataInicio && moment(o.dataInicio).isValid()
                ? moment(o.dataInicio).format("DD/MM/YYYY")
                : "N/A",
            s: dateStyle,
          },
          {
            v: o.dataTermino
              ? moment(o.dataTermino).format("DD/MM/YYYY")
              : o.dataUltimaExecucao
                ? moment(o.dataUltimaExecucao).format("DD/MM/YYYY")
                : "Em Andamento",
            s: dateStyle,
          },
          totalBeneficiarioFinal,
          totalInpsFinal,
        ];
        xlsRow.orcamentoId = o._id;

        const row = {
          id: o._id,
          idCliente: o.cliente.id,
          nome: o.cliente.nome,
          nrCredencial: o.numeroCredencial || "N/A",
          dataInicio: o.dataInicio
            ? moment(o.dataInicio).format("DD/MM/YYYY")
            : "",
          dataCredencial: moment(o.dataCredencial).format("DD/MM/YYYY"),
          dataTermino: o.dataTermino
            ? moment(o.dataTermino).format("DD/MM/YYYY")
            : o.dataUltimaExecucao
              ? moment(o.dataUltimaExecucao).format("DD/MM/YYYY")
              : "Em Andamento",
          dataTerminoRaw: o.dataTermino ? new Date(o.dataTermino) : new Date(),
          dataUltimaExecucao: o.dataUltimaExecucao
            ? moment(o.dataUltimaExecucao).format("DD/MM/YYYY")
            : null,
          totalBeneficiarioFinal: totalBeneficiarioFinal,
          totalInpsFinal: totalInpsFinal,
          classe: classe,
          jaEnviado: o.envioInstituicao && o.envioInstituicao.enviado,
          dataEnvio:
            o.envioInstituicao && o.envioInstituicao.dataEnvio
              ? moment(o.envioInstituicao.dataEnvio).format("DD/MM/YYYY")
              : "",
          numeroFatura: o.envioInstituicao
            ? o.envioInstituicao.numeroFatura
            : "",
          terminado: o.terminado === true,
          status: o.status,
          categoriaBeneficiario: o.categoriaBeneficiario,
          tratamentosCobertosCompletos: o.tratamentosCobertosCompletos || false,
          statusProcedimentosCobertos:
            o.statusProcedimentosCobertos || "sem_cobertura",
          totalProcedimentosCobertos: o.totalProcedimentosCobertos || 0,
          procedimentosCobertosCompletos: o.procedimentosCobertosCompletos || 0,
          tipoCobertura: o.tipoCobertura || "PARTICULAR",
          desconto: o.desconto || "",
          // majoracao: o.majoracao || "",
          majoracao: o.majoracao,
          totalBeneficiarioSoExecutados: o.totalBeneficiarioSoExecutados || 0,
          totalInstituicaoSoExecutados: o.totalInstituicaoSoExecutados || 0,
        };
        tempCredenciaisData.push(xlsRow);
        tempRows.push(row);
      });

      tempRows.sort((a, b) => a.dataTerminoRaw - b.dataTerminoRaw);

      tempCredenciaisData.forEach((row, index) => {
        row[0] = index + 1;
      });

      const resumoComMajoracao = tempCredenciaisData.reduce(
        (acc, linha) => ({
          totalOrcamentos: acc.totalOrcamentos + 1,
          totalBeneficiario: acc.totalBeneficiario + Number(linha[5] || 0),
          totalInstituicao: acc.totalInstituicao + Number(linha[6] || 0),
        }),
        {
          totalOrcamentos: 0,
          totalBeneficiario: 0,
          totalInstituicao: 0,
        },
      );

      setRows(tempRows);
      setDados({
        dadosTabela: tempCredenciaisData,
        resumo: resumoComMajoracao,
      });

      setSelecionados([]);
    } catch (err) {
      console.error("Erro ao gerar relatório de credenciais:", err);
      setError(
        err.message || "Ocorreu um erro ao gerar o relatório de credenciais.",
      );
    } finally {
      stopLoading();
    }
  };

  const ensureValue = (value) =>
    value || {
      v: "",
      s: {
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      },
    };

  const exportarFicheiro = () => {
    if (!dados || !dados.dadosTabela || dados.dadosTabela.length === 0) {
      toast.error("Sem dados para exportar!");
      return;
    }

    if (selecionados.length > 0) {
      const rowsIds = new Set(selecionados);
      const rowsFiltradas = rows.filter((row) => rowsIds.has(row.id));

      if (rowsFiltradas.length === 0) {
        toast.error(
          "Não foi possível encontrar os orçamentos selecionados. Verifique a seleção.",
        );
        return;
      }

      const dadosTabelaFiltrados = rowsFiltradas.map((row, index) => {
        const dateStyle = {
          alignment: { horizontal: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };

        if (row.classe === "expirou") {
          dateStyle.font = { color: { rgb: "FF0000" } };
        } else if (row.classe === "ajustar") {
          dateStyle.font = { color: { rgb: "FFA500" } };
        }

        const emAndamento =
          !row.jaEnviado &&
          row.status !== "INTERROMPIDO" &&
          row.procedimentosCobertosCompletos > 0 &&
          (row.tipoCobertura === "SEGURADO" || row.tipoCobertura === "MISTO"
            ? !row.tratamentosCobertosCompletos
            : !row.terminado);

        const totalBeneficiario =
          configExportacao.calcularSoExecutados && emAndamento
            ? row.totalBeneficiarioSoExecutados
            : row.totalBeneficiarioFinal;

        const totalInstituicao =
          configExportacao.calcularSoExecutados && emAndamento
            ? row.totalInstituicaoSoExecutados
            : row.totalInpsFinal;

        const linha = [
          index + 1,
          row.nome,
          ensureValue(row.nrCredencial),
          { v: row.dataInicio || "N/A", s: dateStyle },
          {
            v: row.dataUltimaExecucao
              ? row.dataUltimaExecucao
              : row.dataTermino,
            s: dateStyle,
          },
          totalBeneficiario,
          totalInstituicao,
        ];
        linha.orcamentoId = row.id;
        return linha;
      });

      const resumoFiltrado = rowsFiltradas.reduce(
        (acc, row) => {
          const emAndamento =
            !row.jaEnviado &&
            row.status !== "INTERROMPIDO" &&
            row.procedimentosCobertosCompletos > 0 &&
            (row.tipoCobertura === "SEGURADO" || row.tipoCobertura === "MISTO"
              ? !row.tratamentosCobertosCompletos
              : !row.terminado);
          const totalBeneficiario =
            configExportacao.calcularSoExecutados && emAndamento
              ? row.totalBeneficiarioSoExecutados
              : row.totalBeneficiarioFinal;
          const totalInstituicao =
            configExportacao.calcularSoExecutados && emAndamento
              ? row.totalInstituicaoSoExecutados
              : row.totalInpsFinal;
          return {
            totalOrcamentos: acc.totalOrcamentos + 1,
            totalBeneficiario: acc.totalBeneficiario + totalBeneficiario,
            totalInstituicao: acc.totalInstituicao + totalInstituicao,
          };
        },
        {
          totalOrcamentos: 0,
          totalBeneficiario: 0,
          totalInstituicao: 0,
        },
      );

      if (dadosTabelaFiltrados.length === 0) {
        toast.error("Sem dados para exportar após filtragem!");
        return;
      }

      gerarRelatorioCredenciais({
        dadosTabela: dadosTabelaFiltrados,
        resumo: resumoFiltrado,
      });
    } else {
      const dadosTabelaReconstruidos = rows.map((row, index) => {
        const dateStyle = {
          alignment: { horizontal: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };

        if (row.classe === "expirou") {
          dateStyle.font = { color: { rgb: "FF0000" } };
        } else if (row.classe === "ajustar") {
          dateStyle.font = { color: { rgb: "FFA500" } };
        }

        const emAndamento =
          !row.jaEnviado &&
          row.status !== "INTERROMPIDO" &&
          row.procedimentosCobertosCompletos > 0 &&
          (row.tipoCobertura === "SEGURADO" || row.tipoCobertura === "MISTO"
            ? !row.tratamentosCobertosCompletos
            : !row.terminado);

        const totalBeneficiario =
          configExportacao.calcularSoExecutados && emAndamento
            ? row.totalBeneficiarioSoExecutados
            : row.totalBeneficiarioFinal;

        const totalInstituicao =
          configExportacao.calcularSoExecutados && emAndamento
            ? row.totalInstituicaoSoExecutados
            : row.totalInpsFinal;

        const linha = [
          index + 1,
          row.nome,
          ensureValue(row.nrCredencial),
          { v: row.dataInicio || "N/A", s: dateStyle },
          {
            v: row.dataUltimaExecucao
              ? row.dataUltimaExecucao
              : row.dataTermino,
            s: dateStyle,
          },
          totalBeneficiario,
          totalInstituicao,
        ];
        linha.orcamentoId = row.id;
        return linha;
      });

      const resumoReconstruido = dadosTabelaReconstruidos.reduce(
        (acc, linha) => ({
          totalOrcamentos: acc.totalOrcamentos + 1,
          totalBeneficiario: acc.totalBeneficiario + Number(linha[5] || 0),
          totalInstituicao: acc.totalInstituicao + Number(linha[6] || 0),
        }),
        {
          totalOrcamentos: 0,
          totalBeneficiario: 0,
          totalInstituicao: 0,
        },
      );

      gerarRelatorioCredenciais({
        dadosTabela: dadosTabelaReconstruidos,
        resumo: resumoReconstruido,
      });
    }
  };

  const gerarRelatorioCredenciais = (dados) => {
    if (!dados || !dados.dadosTabela || dados.dadosTabela.length === 0) {
      toast.error("Sem dados para gerar o relatório!");
      return null;
    }

    try {
      const wb = XLSX.utils.book_new();

      if (configExportacao.organizarPorCategoria) {
        gerarExcelPorCategoria(wb, dados);
      } else {
        gerarExcelSequencial(wb, dados);
      }

      const dataHoje = moment().format("YYYYMMDD");
      const nomeArquivo = `relatorio_credenciais_${dataHoje}.xlsx`;

      if (!wb.SheetNames || wb.SheetNames.length === 0) {
        toast.error("Erro ao gerar relatório: Workbook está vazio");
        return null;
      }

      XLSX.writeFile(wb, nomeArquivo);

      toast.success(`Relatório gerado com sucesso: ${nomeArquivo}`);
      return nomeArquivo;
    } catch (error) {
      toast.error(`Erro ao gerar relatório: ${error.message}`);
      return null;
    }
  };

  const gerarExcelPorCategoria = (wb, dados) => {
    const grupos = agruparPorCategoria(dados.dadosTabela);
    const ordemCategorias = ["TCO", "TCP", "PA", "MBCSS", "SEM_CATEGORIA"];
    const totaisPorCategoria = {};

    ordemCategorias.forEach((categoria) => {
      if (!grupos[categoria] || grupos[categoria].length === 0) return;

      const dadosCategoria = grupos[categoria];
      const folhas = dividirEmFolhas(
        dadosCategoria,
        configExportacao.orcamentosPorFolha,
      );

      const totaisCategoria = calcularTotaisCategoria(dadosCategoria);
      totaisPorCategoria[categoria] = totaisCategoria;

      folhas.forEach((folha, index) => {
        const nomeCategoria =
          categoria === "SEM_CATEGORIA" ? "Sem Categoria" : categoria;
        const nomeFolha =
          folhas.length > 1
            ? `${nomeCategoria} - Parte ${index + 1}`
            : nomeCategoria;

        criarFolhaExcel(wb, folha, nomeFolha, totaisCategoria);
      });
    });

    criarFolhaResumoPorCategoria(wb, totaisPorCategoria, dados.resumo);
    criarFolhaResumoGeral(wb, dados.resumo);
  };

  const gerarExcelSequencial = (wb, dados) => {
    const rowsPerSheet = configExportacao.orcamentosPorFolha;
    const totalRows = dados.dadosTabela.length;
    const totalSheets = Math.ceil(totalRows / rowsPerSheet);

    if (totalSheets <= 0) {
      console.error("Nenhuma folha para criar");
      toast.error("Sem dados suficientes para gerar o relatório!");
      return null;
    }

    for (let i = 0; i < totalSheets; i++) {
      const startIdx = i * rowsPerSheet;
      const endIdx = Math.min(startIdx + rowsPerSheet, totalRows);
      const dadosParciais = dados.dadosTabela.slice(startIdx, endIdx);

      const pageSubtotals = calcularTotaisCategoria(dadosParciais);

      const nomeFolha = totalSheets > 1 ? `Parte ${i + 1}` : "Relatório";
      criarFolhaExcel(wb, dadosParciais, nomeFolha, pageSubtotals);
    }

    criarFolhaResumoGeral(wb, dados.resumo);
  };

  // const agruparPorCategoria = (dadosTabela) => {
  //   const rowsMap = {};
  //   rows.forEach((row) => {
  //     rowsMap[row.nome + "_" + row.nrCredencial] = row;
  //   });

  //   const dadosComCategoria = dadosTabela.map((linha) => {
  //     const nomeCliente = linha[1];
  //     const nrCredencial = linha[2]?.v || linha[2] || "N/A";
  //     const chave = nomeCliente + "_" + nrCredencial;
  //     const row = rowsMap[chave];

  //     const linhaArray = Array.isArray(linha)
  //       ? [...linha]
  //       : Object.values(linha);
  //     linhaArray.categoriaBeneficiario = row?.categoriaBeneficiario || null;

  //     return linhaArray;
  //   });

  //   return dadosComCategoria.reduce((grupos, linha) => {
  //     const categoria = linha.categoriaBeneficiario || "SEM_CATEGORIA";
  //     if (!grupos[categoria]) grupos[categoria] = [];

  //     const linhaLimpa = [...linha];
  //     delete linhaLimpa.categoriaBeneficiario;

  //     grupos[categoria].push(linhaLimpa);
  //     return grupos;
  //   }, {});
  // };

  const agruparPorCategoria = (dadosTabela) => {
    // Lookup por id do orçamento — único garantido
    const rowsMap = {};
    rows.forEach((row) => {
      rowsMap[row.id] = row;
    });

    return dadosTabela.reduce((grupos, linha) => {
      const row = rowsMap[linha.orcamentoId];
      const categoria = row?.categoriaBeneficiario || "SEM_CATEGORIA";

      if (!grupos[categoria]) grupos[categoria] = [];

      // Preserva a propriedade orcamentoId na cópia (caso seja necessária depois)
      const linhaCopia = [...linha];
      linhaCopia.orcamentoId = linha.orcamentoId;

      grupos[categoria].push(linhaCopia);
      return grupos;
    }, {});
  };

  const dividirEmFolhas = (grupo, limite) => {
    const folhas = [];
    for (let i = 0; i < grupo.length; i += limite) {
      folhas.push(grupo.slice(i, i + limite));
    }
    return folhas;
  };

  const calcularTotaisCategoria = (dadosCategoria) => {
    return dadosCategoria.reduce(
      (acc, linha) => ({
        totalBeneficiario: acc.totalBeneficiario + Number(linha[5] || 0),
        totalInstituicao: acc.totalInstituicao + Number(linha[6] || 0),
      }),
      {
        totalBeneficiario: 0,
        totalInstituicao: 0,
      },
    );
  };

  const criarFolhaExcel = (wb, dadosParciais, nomeFolha, pageSubtotals) => {
    if (!Array.isArray(dadosParciais)) {
      console.error("❌ dadosParciais não é array:", dadosParciais);
      throw new Error("dadosParciais deve ser um array");
    }

    const dadosVerificados = dadosParciais.map((linha, index) => {
      if (!Array.isArray(linha)) {
        console.error(`❌ Linha ${index} não é array:`, linha);
        if (typeof linha === "object" && linha !== null) {
          return Object.values(linha);
        }
        throw new Error(`Linha ${index} não é um array válido`);
      }
      return linha;
    });

    const ws = XLSX.utils.aoa_to_sheet([]);

    const totalRow = 8 + dadosVerificados.length + 1;

    const dadosCompletos = [
      [dadosClinica.nomeFiscal, "", "", "", "", "", ""],
      ["", "", "", "", "", "", ""],
      ["Nº Fatura: ......"],
      ["Mês de Referência: ......"],
      [
        `Relação de Beneficiários atendidos: ${moment(dataFiltro.dataFinal).subtract(1, "month").format("MMMM YYYY")} (${nomeFolha})`,
      ],
      [],
      [
        "Nº",
        "Nome do Beneficiário",
        "Nº Processo",
        "Tratamento",
        "",
        "Total Beneficiário",
        `Total ${instituicao?.nome || "Instituição"}`,
      ],
      ["", "", "", "Início", "Conclusão", "", ""],
      ...dadosVerificados,
      [],
      [
        {
          v: "Total (desta página)",
          s: {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: "center" },
          },
        },
        "",
        "",
        "",
        "",
        {
          v: pageSubtotals.totalBeneficiario.toLocaleString("pt-BR"),
          s: {
            font: { bold: true },
            alignment: { horizontal: "center", vertical: "center" },
          },
        },
        {
          v: pageSubtotals.totalInstituicao.toLocaleString("pt-BR"),
          s: {
            font: { bold: true },
            alignment: { horizontal: "center", vertical: "center" },
          },
        },
      ],
      [
        {
          v: moment().locale("pt-br").format("D [de] MMMM [de] YYYY"),
          s: { alignment: { horizontal: "center", vertical: "center" } },
        },
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      [
        {
          v: "......",
          s: { alignment: { horizontal: "center", vertical: "center" } },
        },
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      [
        {
          v: `${dadosClinica.endereco} - / ${dadosClinica.contacto} - / NIF - ${dadosClinica.nif} - / ${
            dadosClinica?.codigoPostal ? dadosClinica?.codigoPostal : "2110"
          } - / ${dadosClinica.email}`,
          s: { alignment: { horizontal: "center", vertical: "center" } },
        },
        "",
        "",
        "",
        "",
        "",
        "",
      ],
    ];

    XLSX.utils.sheet_add_aoa(ws, dadosCompletos);

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 6 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 6 } },
      { s: { r: 6, c: 3 }, e: { r: 6, c: 4 } },
      { s: { r: totalRow, c: 0 }, e: { r: totalRow, c: 4 } },
      { s: { r: totalRow + 1, c: 0 }, e: { r: totalRow + 1, c: 6 } },
      { s: { r: totalRow + 2, c: 0 }, e: { r: totalRow + 2, c: 6 } },
      { s: { r: totalRow + 3, c: 0 }, e: { r: totalRow + 3, c: 6 } },
    ];

    const titleStyle = {
      font: { bold: true, sz: 22 },
      alignment: { horizontal: "center" },
    };

    const tableHeaderStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    ws["A1"].s = titleStyle;
    ws["A2"].s = titleStyle;
    ws["A5"].s = { alignment: { horizontal: "center" } };

    for (let c = 0; c < 7; c++) {
      ws[XLSX.utils.encode_cell({ r: 6, c })].s = tableHeaderStyle;
      ws[XLSX.utils.encode_cell({ r: 7, c })].s = tableHeaderStyle;
    }

    const borderStyle = {
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const numLinhas = dadosVerificados.length + 9;
    for (let r = 8; r < numLinhas; r++) {
      for (let c = 0; c < 7; c++) {
        const cell = XLSX.utils.encode_cell({ r, c });
        ws[cell] = ws[cell] || {};
        ws[cell].s = { ...(ws[cell].s || {}), ...borderStyle };
      }
    }

    ws["!cols"] = [
      { wch: 5 },
      { wch: 40 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
      { wch: 20 },
    ];

    ws["!print"] = {
      printArea: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: numLinhas + 6, c: 6 },
      }),
    };

    ws["!pageSetup"] = {
      orientation: "landscape",
      paperSize: 9,
    };

    XLSX.utils.book_append_sheet(wb, ws, nomeFolha);
  };

  const criarFolhaResumoPorCategoria = (
    wb,
    totaisPorCategoria,
    resumoGeral,
  ) => {
    const dadosResumo = [];

    dadosResumo.push([
      dadosClinica.nomeFiscal,
      "",
      "",
      "",
      `NIF - ${dadosClinica.nif}`,
    ]);
    dadosResumo.push(["", "", "", "", "N° Prestador -"]);
    dadosResumo.push(["Nº Fatura: ......"]);
    dadosResumo.push(["Mês de Referência: ......"]);
    dadosResumo.push(["Resumo por Categoria de Beneficiário"]);
    dadosResumo.push([]);

    dadosResumo.push([
      "Categoria",
      "Qtd Orçamentos",
      "Total Beneficiário",
      `Total ${instituicao?.nome || "Instituição"}`,
    ]);

    Object.entries(totaisPorCategoria).forEach(([categoria, totais]) => {
      const nomeCategoria =
        categoria === "SEM_CATEGORIA" ? "Sem Categoria" : categoria;
      dadosResumo.push([
        nomeCategoria,
        "—",
        totais.totalBeneficiario.toLocaleString("pt-BR"),
        totais.totalInstituicao.toLocaleString("pt-BR"),
      ]);
    });

    dadosResumo.push([]);
    dadosResumo.push([
      "TOTAL GERAL",
      resumoGeral.totalOrcamentos,
      resumoGeral.totalBeneficiario.toLocaleString("pt-BR"),
      resumoGeral.totalInstituicao.toLocaleString("pt-BR"),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(dadosResumo);

    const headerStyle = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center" },
    };

    ws["A1"].s = headerStyle;
    ws["A5"].s = headerStyle;

    ws["!cols"] = [{ wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 }];

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Resumo por Categoria");
  };

  const criarFolhaResumoGeral = (wb, resumo) => {
    const summaryWS = XLSX.utils.aoa_to_sheet([
      [dadosClinica.nomeFiscal, "", "", "", `NIF - ${dadosClinica.nif}`],
      ["", "", "", "", "N° Prestador -"],
      ["Nº Fatura: ......"],
      ["Mês de Referência: ......"],
      ["Resumo - Totais Gerais"],
      [],
      [
        "TOTAL GERAL BENEFICIÁRIO",
        "",
        "",
        "",
        resumo.totalBeneficiario.toLocaleString("pt-BR"),
      ],
      [
        `TOTAL GERAL ${instituicao?.nome || "Instituição"}`,
        "",
        "",
        "",
        resumo.totalInstituicao.toLocaleString("pt-BR"),
      ],
      [
        "VALOR TOTAL",
        "",
        "",
        "",
        (
          Number(resumo.totalBeneficiario) + Number(resumo.totalInstituicao)
        ).toLocaleString("pt-BR"),
      ],
      [],
      [
        {
          v: moment().locale("pt-br").format("D [de] MMMM [de] YYYY"),
          s: { alignment: { horizontal: "center", vertical: "center" } },
        },
        "",
        "",
        "",
        "",
      ],
    ]);

    const headerStyle = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center" },
    };

    summaryWS["A1"].s = headerStyle;
    summaryWS["A5"].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center" },
    };

    summaryWS["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
      { s: { r: 6, c: 0 }, e: { r: 6, c: 3 } },
      { s: { r: 7, c: 0 }, e: { r: 7, c: 3 } },
      { s: { r: 8, c: 0 }, e: { r: 8, c: 3 } },
      { s: { r: 10, c: 0 }, e: { r: 10, c: 4 } },
    ];

    summaryWS["!cols"] = [
      { wch: 40 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
    ];

    XLSX.utils.book_append_sheet(wb, summaryWS, "Resumo Geral");
  };

  const fecharDialogoEnvio = () => {
    setDialogoEnvioAberto(false);
  };

  const marcarComoEnviados = async () => {
    if (selecionados.length === 0) {
      toast.error("Nenhum orçamento selecionado!");
      return;
    }

    if (!dadosEnvio.numeroFatura || !dadosEnvio.mesReferencia) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }

    startLoading();
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/marcarOrcamentosEnviadosInstituicao/${dadosClinica._id}`,
        "POST",
        JSON.stringify({
          orcamentoIds: selecionados,
          numeroFatura: dadosEnvio.numeroFatura,
          mesReferencia: dadosEnvio.mesReferencia,
          userId: auth.userId,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      if (responseData.success) {
        toast.success(
          `${responseData.resultado.modifiedCount} orçamentos marcados como enviados à Intituição!`,
        );

        exportarFicheiro();
        gerarRelatorio();
        fecharDialogoEnvio();
      } else {
        toast.error(
          responseData.message || "Erro ao marcar orçamentos como enviados",
        );
      }
    } catch (err) {
      console.error("Erro ao marcar orçamentos como enviados:", err);
      toast.error(
        err.message || "Ocorreu um erro ao marcar orçamentos como enviados",
      );
    } finally {
      stopLoading();
    }
  };

  const desmarcarComoEnviados = async () => {
    if (selecionados.length === 0) {
      toast.error("Nenhum orçamento selecionado!");
      return;
    }

    startLoading();
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/desmarcarOrcamentosEnviadosInstituicao/${dadosClinica._id}`,
        "POST",
        JSON.stringify({
          orcamentoIds: selecionados,
          userId: auth.userId,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      if (responseData.success) {
        toast.success(
          `${responseData.resultado.modifiedCount} orçamento(s) desmarcado(s) como enviado(s)!`,
        );
        setDialogoDesmarcarAberto(false);
        gerarRelatorio();
      } else {
        toast.error(responseData.message || "Erro ao desmarcar orçamentos");
      }
    } catch (err) {
      console.error("Erro ao desmarcar orçamentos:", err);
      toast.error(err.message || "Ocorreu um erro ao desmarcar orçamentos");
    } finally {
      stopLoading();
    }
  };

  const columns = [
    {
      field: "terminado",
      headerName: "Status",
      width: 110,
      renderCell: (params) => {
        const row = params.row;

        if (row.jaEnviado) {
          return (
            <Box
              sx={{
                backgroundColor: "#9e9e9e",
                color: "white",
                borderRadius: "16px",
                padding: "4px 10px",
                fontSize: "0.75rem",
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Enviado
            </Box>
          );
        }
        if (row.status === "INTERROMPIDO") {
          if (
            row.tipoCobertura === "SEGURADO" ||
            row.tipoCobertura === "MISTO"
          ) {
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.3,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#f44336",
                    color: "white",
                    borderRadius: "16px",
                    padding: "4px 10px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Interrompido
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                >
                  {row.procedimentosCobertosCompletos}/
                  {row.totalProcedimentosCobertos}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                backgroundColor: "#f44336",
                color: "white",
                borderRadius: "16px",
                padding: "4px 10px",
                fontSize: "0.75rem",
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Interrompido
            </Box>
          );
        }

        if (row.tipoCobertura === "SEGURADO" || row.tipoCobertura === "MISTO") {
          if (row.tratamentosCobertosCompletos) {
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.3,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "white",
                    borderRadius: "16px",
                    padding: "4px 10px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Pronto
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                >
                  {row.procedimentosCobertosCompletos}/
                  {row.totalProcedimentosCobertos}
                </Typography>
              </Box>
            );
          }

          if (row.procedimentosCobertosCompletos > 0) {
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.3,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#ff9800",
                    color: "white",
                    borderRadius: "16px",
                    padding: "4px 10px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Parcial
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                >
                  {row.procedimentosCobertosCompletos}/
                  {row.totalProcedimentosCobertos}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.3,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#9e9e9e",
                  color: "white",
                  borderRadius: "16px",
                  padding: "4px 10px",
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Pendente
              </Box>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.65rem", color: "text.secondary" }}
              >
                0/{row.totalProcedimentosCobertos}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              backgroundColor: row.terminado ? "#4caf50" : "#ff9800",
              color: "white",
              borderRadius: "16px",
              padding: "4px 10px",
              fontSize: "0.75rem",
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {row.terminado ? "Terminado" : "Em Andamento"}
          </Box>
        );
      },
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 7,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.nome} placement="top" arrow>
            <span
              className={`nomeDataTable ${
                params.row.jaEnviado ? "enviado" : ""
              }`}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                display: "block",
              }}
            >
              <Link to={`/clientes/${params.row.idCliente}-orcamento`}>
                {params.row.nome}
              </Link>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "nrCredencial",
      headerName: "Nr Credencial",
      flex: 3,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <span style={{ flex: 1 }}>{params.row.nrCredencial}</span>
            <Tooltip title="Editar dados da credencial" placement="top">
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  abrirModalEditarCredencial(params.row);
                }}
                sx={{ minWidth: "auto", p: 0.5 }}
              >
                ✏️
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "dataCredencial",
      headerName: "Dt Emissão",
      flex: 3,
      renderCell: (params) => {
        return (
          <span className={params.row.classe ? params.row.classe : ""}>
            {params.row.dataCredencial || "N/A"}
          </span>
        );
      },
    },
    {
      field: "dataInicio",
      headerName: "Dt Inicio",
      flex: 3,
      renderCell: (params) => {
        return (
          <span className={params.row.classe ? params.row.classe : ""}>
            {params.row.dataInicio ? params.row.dataInicio : "N/A"}
          </span>
        );
      },
    },
    {
      field: "dataTermino",
      headerName: "Dt Término",
      flex: 3,
      renderCell: (params) => {
        return (
          <span className={params.row.classe ? params.row.classe : ""}>
            {params.row.dataTermino}
          </span>
        );
      },
    },
    {
      field: "totalBeneficiario",
      headerName: "Total Beneficiário",
      flex: 3,
      renderCell: (params) => {
        return (
          <span>
            {params.row.totalBeneficiarioFinal.toLocaleString("pt-BR")}
          </span>
        );
      },
    },
    {
      field: "totalInstituicao",
      headerName: `Total ${instituicao?.nome || "Instutuição"}`,
      flex: 3,
      renderCell: (params) => {
        return <span>{params.row.totalInpsFinal.toLocaleString("pt-BR")}</span>;
      },
    },
    {
      field: "desconto",
      headerName: "Desconto",
      flex: 2,
      renderCell: (params) => {
        return (
          <span>
            {params.row.desconto
              ? params.row.desconto.toLocaleString("pt-BR")
              : ""}
          </span>
        );
      },
    },
    {
      field: "majoracao",
      headerName: "Majoração",
      flex: 2,
      renderCell: (params) => {
        return (
          <span>
            {params.row.majoracao
              ? params.row.majoracao.toLocaleString("pt-BR")
              : ""}
          </span>
        );
      },
    },
    ...(filtroStatus === "enviados"
      ? [
          {
            field: "dataEnvio",
            headerName: "Data Envio",
            flex: 3,
            renderCell: (params) => {
              return <span>{params.row.dataEnvio || "—"}</span>;
            },
          },
          {
            field: "numeroFatura",
            headerName: "Nº Fatura",
            flex: 2,
            renderCell: (params) => {
              return <span>{params.row.numeroFatura || "—"}</span>;
            },
          },
        ]
      : []),
  ];

  const handleChangeSelectInstituicao = (event) => {
    setSelectedInstituicao(event.target.value);
  };

  const abrirDialogoExportacao = () => {
    setConfigExportacao({
      organizarPorCategoria: false,
      orcamentosPorFolha: 15,
      calcularSoExecutados: true,
    });
    setDialogoExportacaoAberto(true);
  };

  const fecharDialogoExportacao = () => {
    setDialogoExportacaoAberto(false);
  };

  const confirmarExportacao = () => {
    exportarFicheiro();
    fecharDialogoExportacao();
  };

  const abrirDialogoEnvio = async () => {
    if (selecionados.length === 0) {
      toast.error(
        `Selecione pelo menos um orçamento para enviar à ${
          instituicao?.nome || "Instutuição"
        }!`,
      );
      return;
    }

    startLoading();
    let numeroFaturaInicial = "";

    try {
      const formData = new FormData();
      formData.append("tipo", "FTE");
      formData.append("ano", new Date().getFullYear().toString());

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/ultimoEmitido/${dadosClinica._id}`,
        "POST",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      const ultimoNr = responseData.nrDocumento;
      numeroFaturaInicial = Number(ultimoNr) + 1;
    } catch (err) {
      console.error("Erro ao buscar último número de FTE:", err);
    } finally {
      stopLoading();
    }

    setDadosEnvio({
      numeroFatura: numeroFaturaInicial.toString(),
      mesReferencia: moment().format("MMMM YYYY"),
    });

    setConfigExportacao({
      organizarPorCategoria: false,
      orcamentosPorFolha: 15,
      calcularSoExecutados: true,
    });

    setDialogoEnvioAberto(true);
  };

  const abrirDialogComprovativoDesconto = () => {
    setDialogCompravitoDescontoAberto(true);
  };

  const fecharDialogComprovativoDesconto = () => {
    setDialogCompravitoDescontoAberto(false);
  };

  const confirmarComprovativoDesconto = () => {
    const filtrados = rows
      .filter((row) => selecionados.includes(row.id))
      .filter((res) => res.desconto && Number(res.desconto) > 0);

    gerarPdfComprovativoDesconto(dataComprovativo, filtrados);

    fecharDialogComprovativoDesconto();
  };

  const abrirModalEditarCredencial = (row) => {
    // Converter a data de string DD/MM/YYYY para momento válido
    const dataCredencialMoment = row.dataCredencial
      ? moment(row.dataCredencial, "DD/MM/YYYY")
      : moment();

    setDadosCredencialEdicao({
      idOrcamento: row.id,
      dataInicial: dataCredencialMoment.toISOString(), // ou .format() conforme esperado
      nrCredencialInicial: row.nrCredencial,
      categoriaInicial: row.categoriaBeneficiario,
    });
    setModalCredencialAberto(true);
  };

  const handleUpdateCredencialData = (
    nrCredencial,
    dataCredencial,
    idOrcamento,
    categoria,
  ) => {
    const novaDataFormatada = moment(dataCredencial).format("DD/MM/YYYY");
    const novaDataISO = moment(dataCredencial).toISOString();

    // Atualizar rows — usar prevRows para garantir o estado mais recente
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== idOrcamento) return row;

        const novaClasse = calcularClasseCredencial({
          dataCredencial: novaDataISO,
          dataInicio: row.dataInicio
            ? moment(row.dataInicio, "DD/MM/YYYY").toISOString()
            : null,
          jaEnviado: row.jaEnviado,
        });

        return {
          ...row,
          nrCredencial,
          dataCredencial: novaDataFormatada,
          categoriaBeneficiario: categoria || row.categoriaBeneficiario,
          classe: novaClasse,
        };
      }),
    );

    // Atualizar dados.dadosTabela usando o orcamentoId embutido na linha
    setDados((prevDados) => {
      if (!prevDados) return prevDados;

      const updatedTabela = prevDados.dadosTabela.map((linha) => {
        if (linha.orcamentoId !== idOrcamento) return linha;

        // Cópia preservando a propriedade orcamentoId
        const novaLinha = [...linha];
        novaLinha.orcamentoId = linha.orcamentoId;
        novaLinha[2] = ensureValue(nrCredencial);
        novaLinha.categoriaBeneficiario = categoria;
        return novaLinha;
      });

      return {
        ...prevDados,
        dadosTabela: updatedTabela,
      };
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          component="div"
          sx={{ fontWeight: "bold" }}
        >
          Relatório de Credenciais para {instituicao?.nome || "Instutuição"}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Período: {dataFiltro.dataInicial.format("DD/MM/YYYY")} a{" "}
            {dataFiltro.dataFinal.format("DD/MM/YYYY")}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Clínica: {dadosClinica?.nome || ""}
          </Typography>

          {instituicoes && instituicoes.length > 0 && selectedInstituicao && (
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mt: 1, maxWidth: "400px", display: "flex" }}
            >
              <InputLabel id="select-label">
                Selecione uma Instituição
              </InputLabel>
              <Select
                labelId="select-label"
                id="select"
                value={selectedInstituicao}
                onChange={handleChangeSelectInstituicao}
                label="Selecione uma Instituição"
                input={<OutlinedInput label="Selecione uma Instituição" />}
              >
                {instituicoes.map((i) => (
                  <MenuItem value={i.value} key={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl component="fieldset" sx={{ mt: 2, display: "flex" }}>
            <FormLabel component="legend">Status dos Orçamentos</FormLabel>
            <RadioGroup
              row
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <FormControlLabel
                value="todos"
                control={<Radio />}
                label="Todos"
              />
              <FormControlLabel
                value="terminados"
                control={<Radio />}
                label="Prontos para Enviar"
              />
              <FormControlLabel
                value="em_andamento"
                control={<Radio />}
                label="Em Andamento"
              />
              <FormControlLabel
                value="expirados"
                control={<Radio />}
                label="Expirados"
              />
              <FormControlLabel
                value="enviados"
                control={<Radio />}
                label="Enviados"
              />
            </RadioGroup>
          </FormControl>

          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={gerarRelatorio}
            >
              Aplicar Filtros
            </Button>

            {dados && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={abrirDialogoExportacao}
                >
                  Exportar{" "}
                  {selecionados.length > 0
                    ? `(${selecionados.length} selecionados)`
                    : "Todos"}
                </Button>

                {filtroStatus !== "enviados" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SendIcon />}
                    onClick={abrirDialogoEnvio}
                    disabled={selecionados.length === 0}
                  >
                    Exportar e marcar Como Enviado à{" "}
                    {instituicao?.nome || "Instutuição"}
                  </Button>
                )}

                {filtroStatus === "enviados" && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => setDialogoDesmarcarAberto(true)}
                    disabled={selecionados.length === 0}
                  >
                    Marcar como Não Enviado ({selecionados.length})
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>

        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={abrirDialogComprovativoDesconto}
            disabled={
              selecionados.length === 0 ||
              !rows
                .filter((row) => selecionados.includes(row.id))
                .some((row) => row.desconto && Number(row.desconto) > 0)
            }
          >
            Gerar Comprovativos de Desconto
          </Button>

          <Button
            sx={{ marginLeft: "15px" }}
            variant="outlined"
            color="secondary"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={() => setDialogoCartaAberto(true)}
          >
            Gerar Carta de Apresentação
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!dados && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por nome do cliente ou nº de credencial..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2, mt: 2 }}
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, display: "flex", color: "text.secondary" }}>
                🔍
              </Box>
            ),
            endAdornment: searchTerm && (
              <Button
                size="small"
                onClick={() => setSearchTerm("")}
                sx={{ minWidth: "auto" }}
              >
                ✕
              </Button>
            ),
          }}
        />

        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {rowsFiltradas.length} resultado(s) encontrado(s)
          </Typography>
        )}

        {dados && (
          <>
            <Box
              sx={{ mb: 3, p: 2, bgcolor: "background.paper", borderRadius: 1 }}
            >
              <Typography variant="h6" gutterBottom>
                Resumo
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total de orçamentos
                  </Typography>
                  <Typography variant="h6">
                    {dados.resumo.totalOrcamentos}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Beneficiário
                  </Typography>
                  <Typography variant="h6">
                    {dados.resumo.totalBeneficiario.toLocaleString("pt-BR")}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total {instituicao?.nome || "Instutuição"}
                  </Typography>
                  <Typography variant="h6">
                    {dados.resumo.totalInstituicao.toLocaleString("pt-BR")}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ height: 630, width: "100%" }}>
              <DataGrid
                rows={rowsFiltradas}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection
                onSelectionModelChange={handleSelectionChange}
                selectionModel={selecaoInterna}
                isRowSelectable={(params) =>
                  filtroStatus === "enviados" ? true : !params.row.jaEnviado
                }
                className="datagrid"
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    fontSize: "0.775rem",
                    fontWeight: 600,
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: "0.8125rem",
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Legenda:
              </Typography>
              <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#FF0000",
                      mr: 1,
                    }}
                  ></Box>
                  <Typography variant="body2">
                    Credencial expirada ({">"} 180 dias desde emissão)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#FFA500",
                      mr: 1,
                    }}
                  ></Box>
                  <Typography variant="body2">
                    Próximo de expirar ({">"} 150 dias desde emissão)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#9C27B0",
                      mr: 1,
                    }}
                  ></Box>
                  <Typography variant="body2">
                    Início 30+ dias após emissão da credencial
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#EEEEEE",
                      mr: 1,
                    }}
                  ></Box>
                  <Typography variant="body2">
                    Já enviado para {instituicao?.nome || "Instutuição"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Orçamentos com Cobertura (Segurado/Misto):
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      ml: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "#4caf50",
                          color: "white",
                          padding: "2px 8px",
                          fontSize: "0.7rem",
                          mr: 1,
                        }}
                      >
                        Pronto para Enviar
                      </Box>
                      <Typography variant="body2">
                        Todos os procedimentos cobertos foram executados
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "#ff9800",
                          color: "white",
                          padding: "2px 8px",
                          fontSize: "0.7rem",
                          mr: 1,
                        }}
                      >
                        Parcial
                      </Box>
                      <Typography variant="body2">
                        Alguns procedimentos cobertos foram executados
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "#9e9e9e",
                          color: "white",
                          padding: "2px 8px",
                          fontSize: "0.7rem",
                          mr: 1,
                        }}
                      >
                        Pendente
                      </Box>
                      <Typography variant="body2">
                        Nenhum procedimento coberto foi executado ainda
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Status Gerais:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      ml: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "#f44336",
                          color: "white",
                          padding: "2px 8px",
                          fontSize: "0.7rem",
                          mr: 1,
                        }}
                      >
                        Interrompido
                      </Box>
                      <Typography variant="body2">
                        Orçamento interrompido (mostra procedimentos cobertos
                        executados)
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "#9e9e9e",
                          color: "white",
                          padding: "2px 8px",
                          fontSize: "0.7rem",
                          mr: 1,
                        }}
                      >
                        Enviado
                      </Box>
                      <Typography variant="body2">
                        Já enviado à instituição
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      <Dialog
        open={dialogoExportacaoAberto}
        onClose={fecharDialogoExportacao}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Configurações de Exportação</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Configure como deseja exportar os{" "}
            {selecionados.length > 0 ? selecionados.length : rows.length}{" "}
            orçamento(s).
          </Typography>

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={configExportacao.organizarPorCategoria}
                  onChange={(e) =>
                    setConfigExportacao({
                      ...configExportacao,
                      organizarPorCategoria: e.target.checked,
                    })
                  }
                />
              }
              label="Organizar por categoria de beneficiário"
            />

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 4, mb: 2 }}
            >
              As folhas serão agrupadas por TCO, TCP, MBCSS e PA.
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={configExportacao.calcularSoExecutados}
                  onChange={(e) =>
                    setConfigExportacao({
                      ...configExportacao,
                      calcularSoExecutados: e.target.checked,
                    })
                  }
                />
              }
              label="Orçamentos em andamento: calcular apenas procedimentos executados"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 4, mb: 2 }}
            >
              Se desmarcado, será usado o valor total do orçamento.
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Orçamentos por folha</InputLabel>
            <Select
              value={configExportacao.orcamentosPorFolha}
              label="Orçamentos por folha"
              onChange={(e) =>
                setConfigExportacao({
                  ...configExportacao,
                  orcamentosPorFolha: e.target.value,
                })
              }
            >
              <MenuItem value={10}>10 orçamentos</MenuItem>
              <MenuItem value={15}>15 orçamentos</MenuItem>
              <MenuItem value={20}>20 orçamentos</MenuItem>
              <MenuItem value={25}>25 orçamentos</MenuItem>
              <MenuItem value={30}>30 orçamentos</MenuItem>
              <MenuItem value={50}>50 orçamentos</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            {configExportacao.organizarPorCategoria
              ? "As folhas serão organizadas primeiro por categoria de beneficiário, depois divididas conforme a quantidade escolhida."
              : "As folhas serão divididas sequencialmente conforme a quantidade escolhida."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogoExportacao} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={confirmarExportacao}
            color="primary"
            variant="contained"
            startIcon={<FileDownloadIcon />}
          >
            Exportar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoEnvioAberto}
        onClose={fecharDialogoEnvio}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Confirmar Envio à {instituicao?.nome || "Instutuição"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Você selecionou {selecionados.length} orçamento(s) para marcar como
            enviado(s) à {instituicao?.nome || "Instutuição"}.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Número da Fatura"
              fullWidth
              margin="normal"
              value={dadosEnvio.numeroFatura}
              onChange={(e) =>
                setDadosEnvio({ ...dadosEnvio, numeroFatura: e.target.value })
              }
              required
            />

            <TextField
              label="Mês de Referência"
              fullWidth
              margin="normal"
              value={dadosEnvio.mesReferencia}
              onChange={(e) =>
                setDadosEnvio({ ...dadosEnvio, mesReferencia: e.target.value })
              }
              required
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Configurações de Exportação
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={configExportacao.organizarPorCategoria}
                onChange={(e) =>
                  setConfigExportacao({
                    ...configExportacao,
                    organizarPorCategoria: e.target.checked,
                  })
                }
              />
            }
            label="Organizar por categoria de beneficiário"
          />

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={configExportacao.calcularSoExecutados}
                  onChange={(e) =>
                    setConfigExportacao({
                      ...configExportacao,
                      calcularSoExecutados: e.target.checked,
                    })
                  }
                />
              }
              label="Orçamentos em andamento: calcular apenas procedimentos executados"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 4, mb: 2 }}
            >
              Se desmarcado, será usado o valor total do orçamento.
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Orçamentos por folha</InputLabel>
            <Select
              value={configExportacao.orcamentosPorFolha}
              label="Orçamentos por folha"
              onChange={(e) =>
                setConfigExportacao({
                  ...configExportacao,
                  orcamentosPorFolha: e.target.value,
                })
              }
            >
              <MenuItem value={10}>10 orçamentos</MenuItem>
              <MenuItem value={15}>15 orçamentos</MenuItem>
              <MenuItem value={20}>20 orçamentos</MenuItem>
              <MenuItem value={25}>25 orçamentos</MenuItem>
              <MenuItem value={30}>30 orçamentos</MenuItem>
              <MenuItem value={50}>50 orçamentos</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            Após a confirmação, estes orçamentos serão marcados como já enviados
            à {instituicao?.nome || "Instutuição"} e não aparecerão em futuras
            buscas (a menos que o filtro "Enviados" seja aplicado).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogoEnvio} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={marcarComoEnviados}
            color="primary"
            variant="contained"
          >
            Confirmar e Exportar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoDesmarcarAberto}
        onClose={() => setDialogoDesmarcarAberto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirmar Desmarcação</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação irá remover a marcação de envio de{" "}
            <strong>{selecionados.length}</strong> orçamento(s).
          </Alert>
          <Typography variant="body2" gutterBottom>
            Os seguintes dados serão apagados destes orçamentos:
          </Typography>
          <ul>
            <li>Número da fatura associada</li>
            <li>Data de envio</li>
            <li>Mês de referência</li>
          </ul>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Os orçamentos voltarão a aparecer nas listas como "não enviados" e
            poderão ser reenviados normalmente. Tens a certeza que queres
            continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogoDesmarcarAberto(false)}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={desmarcarComoEnviados}
            color="warning"
            variant="contained"
          >
            Confirmar Desmarcação
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoComprovativoDescontoAberto}
        onClose={fecharDialogComprovativoDesconto}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Comprovativo de desconto</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt">
            <DatePicker
              label="Data do Comprovativo"
              value={dataComprovativo}
              onChange={(newValue) => setDataComprovativo(newValue)}
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              format="DD/MM/YYYY"
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogComprovativoDesconto} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={confirmarComprovativoDesconto}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {modalCredencialAberto && (
        <ModalEditarDadosCredencial
          open={modalCredencialAberto}
          handleCloseModal={() => setModalCredencialAberto(false)}
          idOrcamento={dadosCredencialEdicao?.idOrcamento}
          dataInicial={dadosCredencialEdicao?.dataInicial}
          nrCredencialInicial={dadosCredencialEdicao?.nrCredencialInicial}
          categoriaInicial={dadosCredencialEdicao?.categoriaInicial}
          beneficio={instituicao?.nome === "INPS" ? "INPS" : "OUTRO"}
          sendRequest={sendRequest}
          token={auth.token}
          handleUpdateCredencialData={handleUpdateCredencialData}
        />
      )}

      <Dialog
        open={dialogoCartaAberto}
        onClose={() => setDialogoCartaAberto(false)}
      >
        <DialogTitle>Carta de Apresentação</DialogTitle>
        <DialogContent style={{ minWidth: "350px", paddingTop: "20px" }}>
          <TextField
            fullWidth
            label="Data da Carta"
            type="date"
            value={dataCartaApresentacao}
            onChange={(e) => setDataCartaApresentacao(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoCartaAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setMostrarCartaApresentacao(true);
              setDialogoCartaAberto(false);
            }}
          >
            Gerar PDF
          </Button>
        </DialogActions>
      </Dialog>

      {mostrarCartaApresentacao && (
        <PdfCartaApresentacao
          clinica={dadosClinica}
          data={dataCartaApresentacao}
          instituicao={selectedInstituicao}
          instituicoes={instituicoes}
          auth={auth}
          onClose={() => setMostrarCartaApresentacao(false)}
        />
      )}
    </Box>
  );
}

export default RelatorioCredenciais;
